import { Component, ElementRef, HostListener, inject, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faXmark } from '@fortawesome/pro-light-svg-icons';
import { ContractObservationsService } from '../../core/services/contract-observations/contract-observations.service';
import { UtcDatePipe } from '../../core/pipes/utc-date.pipe';

export interface DeclarationAvisosContext {
  id: number;
  name?: string;
  client?: { name?: string };
  model?: { name?: string };
}

@Component({
  selector: 'app-declaration-avisos-modal',
  standalone: true,
  imports: [CommonModule, FaIconComponent, UtcDatePipe],
  templateUrl: './declaration-avisos-modal.component.html',
  styleUrl: './declaration-avisos-modal.component.scss',
})
export class DeclarationAvisosModalComponent {
  private readonly contractObservationsService = inject(ContractObservationsService);

  @ViewChild('closeButton') closeButton?: ElementRef<HTMLButtonElement>;

  faXmark = faXmark;

  isOpen = signal(false);
  isLoading = signal(false);
  avisos = signal<any[]>([]);
  declarationContext = signal<DeclarationAvisosContext | null>(null);
  loadError = signal<string | null>(null);

  private triggerElement: HTMLElement | null = null;

  async open(declaration: DeclarationAvisosContext): Promise<void> {
    this.triggerElement = document.activeElement as HTMLElement;
    this.declarationContext.set(declaration);
    this.isOpen.set(true);
    this.loadError.set(null);
    this.avisos.set([]);
    document.body.classList.add('modal-open');

    await this.loadAvisos(declaration.id);

    setTimeout(() => this.closeButton?.nativeElement.focus());
  }

  close(): void {
    this.isOpen.set(false);
    this.declarationContext.set(null);
    this.avisos.set([]);
    this.loadError.set(null);
    document.body.classList.remove('modal-open');

    if (this.triggerElement) {
      this.triggerElement.focus();
      this.triggerElement = null;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen()) {
      this.close();
    }
  }

  get modalTitle(): string {
    const ctx = this.declarationContext();
    if (!ctx) return 'Avisos de la declaración';

    const parts: string[] = [];
    if (ctx.client?.name) parts.push(ctx.client.name);
    if (ctx.name) parts.push(ctx.name);

    return parts.length > 0 ? `Avisos — ${parts.join(' · ')}` : 'Avisos de la declaración';
  }

  private async loadAvisos(declarationId: number): Promise<void> {
    try {
      this.isLoading.set(true);
      const avisos = await this.contractObservationsService.getVisibleAvisosForDeclaration(declarationId);
      this.avisos.set(avisos || []);
    } catch {
      this.loadError.set('No se han podido cargar los avisos. Inténtalo de nuevo.');
      this.avisos.set([]);
    } finally {
      this.isLoading.set(false);
    }
  }
}
